import React, { useState, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { NavGroup } from '../../../config/navigation';
import { NavDropdown } from './NavDropdown';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { isActiveGroup } from '../../../utils/navigation';

interface NavGroupItemProps {
  group: NavGroup;
}

const NavGroupItem: React.FC<NavGroupItemProps> = ({ group }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, close, isOpen);

  const active = isActiveGroup(group, location.pathname);

  if (group.href) {
    return (
      <Link
        to={group.href}
        className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          active
            ? 'text-brand'
            : 'text-text-secondary hover:text-brand'
        }`}
      >
        {group.label}
        {active && (
          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand rounded-full" />
        )}
      </Link>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          active || isOpen
            ? 'text-brand'
            : 'text-text-secondary hover:text-brand'
        }`}
      >
        {group.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
        {active && !isOpen && (
          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand rounded-full" />
        )}
      </button>
      {isOpen && (
        <NavDropdown items={group.items ?? []} onClose={close} />
      )}
    </div>
  );
};

export { NavGroupItem };
